import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `Rp ${Number(n).toLocaleString("id-ID")}`;

const request = async (path, options = {}) => {
  const token = sessionStorage.getItem("kado-token");
  const response = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Something went wrong");
  return data;
};

const loadSnap = (config) =>
  new Promise((resolve, reject) => {
    if (window.snap) return resolve(window.snap);
    if (!config?.snap_js_url || !config?.midtrans_client_key) return reject(new Error("Payment gateway not configured"));
    const existing = document.getElementById("midtrans-snap");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.snap), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = "midtrans-snap";
    script.src = config.snap_js_url;
    script.setAttribute("data-client-key", config.midtrans_client_key);
    script.async = true;
    script.onload = () => resolve(window.snap);
    script.onerror = () => reject(new Error("Failed to load Midtrans Snap"));
    document.body.appendChild(script);
  });

function Header({ user, setUser }) {
  const navigate = useNavigate();
  return (
    <header className="site-header">
      <Link data-testid="brand-link" to="/" className="brand">
        Kado<span>Kita</span>
      </Link>
      <nav data-testid="main-navigation">
        <Link data-testid="nav-shop-link" to="/gift-cards">Shop</Link>
        <Link data-testid="nav-about-link" to="/about">Our story</Link>
        <Link data-testid="nav-contact-link" to="/contact">Contact</Link>
        <Link data-testid="nav-terms-link" to="/terms">Terms</Link>
      </nav>
      <div className="header-actions">
        {user ? (
          <button
            data-testid="logout-button"
            className="text-button"
            onClick={() => {
              sessionStorage.removeItem("kado-token");
              setUser(null);
              navigate("/");
            }}
          >
            Log out
          </button>
        ) : (
          <Link data-testid="login-link" to="/login" className="text-button">Sign in</Link>
        )}
        <Link data-testid="account-link" to={user ? "/account" : "/register"} className="round-mark">
          {user ? "◎" : "↗"}
        </Link>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div>
        <div className="footer-brand">KadoKita</div>
        <p>Small gestures, beautifully delivered.</p>
      </div>
      <div className="footer-links">
        <Link data-testid="footer-about-link" to="/about">About</Link>
        <Link data-testid="footer-terms-link" to="/terms">Terms</Link>
        <Link data-testid="footer-contact-link" to="/contact">Contact</Link>
        <span data-testid="footer-payment-note">Payments securely handled by Midtrans</span>
      </div>
    </footer>
  );
}

function Layout({ children, user, setUser }) {
  return (
    <>
      <Header user={user} setUser={setUser} />
      {children}
      <Footer />
    </>
  );
}

function Home() {
  return (
    <main>
      <section className="hero shell">
        <div className="hero-copy">
          <p className="eyebrow" data-testid="home-eyebrow">DIGITAL GIFTS, MADE HUMAN</p>
          <h1 data-testid="home-heading">A little <em>something</em><br />for someone.</h1>
          <p className="hero-intro" data-testid="home-intro">
            Gift cards that arrive with the feeling still intact. Choose a thoughtful amount, add your words, and send it in seconds.
          </p>
          <Link data-testid="hero-shop-button" className="button primary" to="/gift-cards">Find a gift <span>↗</span></Link>
        </div>
        <div className="hero-art" aria-label="Illustrated gift card artwork">
          <div className="stamp">KADO<br />KITA</div>
          <div className="art-card"><small>FOR YOUR</small><strong>Favourite<br />person</strong><i>with love, always.</i></div>
          <div className="postmark">∞</div>
        </div>
      </section>
      <section className="occasion-band">
        <div className="shell occasion-inner">
          <span data-testid="occasion-label">A GIFT FOR</span>
          <strong data-testid="occasion-text">just because</strong>
          <span>·</span>
          <strong>thank you</strong>
          <span>·</span>
          <strong>the big day</strong>
          <Link data-testid="occasion-shop-link" to="/gift-cards">See all cards →</Link>
        </div>
      </section>
      <section className="shell feature-section">
        <div className="section-heading">
          <p className="eyebrow">THE SHORTLIST</p>
          <h2>Good gifts,<br /><em>no guesswork.</em></h2>
          <Link data-testid="featured-view-all-link" to="/gift-cards" className="underlined-link">View all cards ↗</Link>
        </div>
        <Catalog preview />
      </section>
      <section className="how-it-works shell">
        <p className="eyebrow">HOW IT WORKS</p>
        <div className="steps">
          <div><b>01</b><h3>Choose a feeling</h3><p>Pick a card that says what you mean, or let them choose their own.</p></div>
          <div><b>02</b><h3>Make it yours</h3><p>Add a name, an email, and a small note from you.</p></div>
          <div><b>03</b><h3>Send the good stuff</h3><p>Pay safely with Midtrans. Their gift code arrives right away by email.</p></div>
        </div>
      </section>
    </main>
  );
}

function Catalog({ preview = false }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  useEffect(() => { request("/catalog").then(setItems).catch(() => setItems([])); }, []);
  const categories = ["All", ...new Set(items.map((item) => item.category))];
  const shown = items.filter((item) => filter === "All" || item.category === filter).slice(0, preview ? 3 : 20);
  return (
    <div className="catalog-wrap">
      {!preview && (
        <div className="filter-row">
          {categories.map((category) => (
            <button
              data-testid={`catalog-filter-${category.toLowerCase().replaceAll(" ", "-")}`}
              className={filter === category ? "filter active" : "filter"}
              key={category}
              onClick={() => setFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
      )}
      <div className="catalog-grid">
        {shown.map((item, index) => (
          <Link
            data-testid={`gift-card-${item.slug}`}
            className={`gift-tile ${item.accent} tile-${index}`}
            key={item.id}
            to={`/gift-cards/${item.slug}`}
          >
            <div className="tile-top"><span>{item.category}</span><span>↗</span></div>
            <div><h3>{item.name}</h3><p>{item.note}</p></div>
            <div className="tile-bottom"><span>{money(item.amount)}</span><span className="mini-arrow">→</span></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Shop() {
  return (
    <main className="shell page">
      <div className="page-intro">
        <p className="eyebrow">THE KADOKITA SHOP</p>
        <h1 data-testid="shop-heading">Find their<br /><em>kind of happy.</em></h1>
        <p>Cards for small celebrations, big thank-yous, and everything in between.</p>
      </div>
      <Catalog />
    </main>
  );
}

function Product() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    request("/catalog").then((items) => setItem(items.find((entry) => entry.slug === slug)));
  }, [slug]);

  if (!item) return <main className="shell page"><p data-testid="product-loading">Loading card…</p></main>;

  const buy = () => {
    sessionStorage.setItem("kado-cart", JSON.stringify({ ...item, recipient_email: email, message }));
    navigate("/checkout");
  };

  return (
    <main className="shell page product-page">
      <Link data-testid="product-back-link" className="back-link" to="/gift-cards">← Back to cards</Link>
      <div className="product-layout">
        <div className={`product-art ${item.accent}`}>
          <small>KADOKITA / DIGITAL GIFT</small>
          <h1>{item.name}</h1>
          <p>{item.note}</p>
          <div className="product-mark">✳</div>
        </div>
        <div className="purchase-form">
          <p className="eyebrow">MAKE IT PERSONAL</p>
          <h2>{item.name}</h2>
          <div className="price" data-testid="product-price">{money(item.amount)}</div>
          <label htmlFor="recipient-email">Recipient email</label>
          <input
            data-testid="recipient-email-input"
            id="recipient-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@example.com"
          />
          <label htmlFor="gift-message">A note, if you like <span>(optional)</span></label>
          <textarea
            data-testid="gift-message-input"
            id="gift-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength="240"
            placeholder="You deserve a little treat…"
          />
          <button
            data-testid="add-to-cart-button"
            className="button primary full"
            onClick={buy}
            disabled={!email}
          >
            Continue to checkout <span>→</span>
          </button>
          <p className="fine-print">Secure payment through Midtrans · Card is emailed to the recipient</p>
        </div>
      </div>
    </main>
  );
}

function Auth({ mode, setUser }) {
  const isLogin = mode === "login";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await request(`/auth/${isLogin ? "login" : "register"}`, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      sessionStorage.setItem("kado-token", data.token);
      setUser(data.user);
      const cart = sessionStorage.getItem("kado-cart");
      navigate(cart ? "/checkout" : "/gift-cards");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-panel">
        <p className="eyebrow">{isLogin ? "WELCOME BACK" : "A PLACE FOR YOUR ORDERS"}</p>
        <h1>{isLogin ? <>Good to<br /><em>see you.</em></> : <>Keep the<br /><em>good stuff.</em></>}</h1>
        <p>{isLogin ? "Sign in to see your gifts and continue shopping." : "Create an account to keep track of every thoughtful thing."}</p>
        <form onSubmit={submit}>
          <label htmlFor="auth-email">Email address</label>
          <input
            data-testid="auth-email-input"
            id="auth-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label htmlFor="auth-password">Password</label>
          <input
            data-testid="auth-password-input"
            id="auth-password"
            type="password"
            autoComplete={isLogin ? "current-password" : "new-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength="8"
            required
          />
          <p className="form-hint">At least 8 characters</p>
          {error && <p data-testid="auth-error" className="error-message">{error}</p>}
          <button data-testid="auth-submit-button" className="button primary full" disabled={busy}>
            {busy ? "Working…" : isLogin ? "Sign in" : "Create account"} <span>→</span>
          </button>
        </form>
        <p className="switch-auth">
          {isLogin ? "New to KadoKita?" : "Already have an account?"}{" "}
          <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"}>
            {isLogin ? "Create one" : "Sign in"}
          </Link>
        </p>
      </div>
    </main>
  );
}

function Checkout({ user, config }) {
  const [cart] = useState(() => JSON.parse(sessionStorage.getItem("kado-cart") || "null"));
  const [email, setEmail] = useState(cart?.recipient_email || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!cart)
    return (
      <main className="shell page">
        <h1>Your basket is empty.</h1>
        <Link data-testid="empty-cart-shop-link" className="button primary" to="/gift-cards">Browse cards</Link>
      </main>
    );

  const pay = async () => {
    setError("");
    if (!user) {
      navigate("/login");
      return;
    }
    if (!config) {
      setError("Payment gateway not ready. Please refresh and try again.");
      return;
    }
    setBusy(true);
    try {
      const checkout = await request("/orders/checkout", {
        method: "POST",
        body: JSON.stringify({
          product_id: cart.id,
          recipient_email: email,
          message: cart.message || "",
        }),
      });
      const snap = await loadSnap(config);
      snap.pay(checkout.snap_token, {
        onSuccess: () => {
          sessionStorage.setItem("kado-order-id", checkout.order_id);
          sessionStorage.removeItem("kado-cart");
          navigate("/order-success");
        },
        onPending: () => {
          sessionStorage.setItem("kado-order-id", checkout.order_id);
          sessionStorage.removeItem("kado-cart");
          navigate("/order-success");
        },
        onError: () => {
          setError("Payment failed. Please try again or use another card.");
          setBusy(false);
        },
        onClose: () => {
          setError("Payment window closed before completion.");
          setBusy(false);
        },
      });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const modeLabel = config?.midtrans_is_production ? "Live payment" : "Midtrans Sandbox";

  return (
    <main className="shell page checkout-page">
      <div className="checkout-main">
        <p className="eyebrow">ONE LAST THING</p>
        <h1>Ready to send<br /><em>some good?</em></h1>
        <p className="checkout-note">
          {user ? `Buying as ${user.email}` : "You'll need an account to keep your order safe."}
        </p>
        <label htmlFor="checkout-email">Recipient email</label>
        <input
          data-testid="checkout-recipient-input"
          id="checkout-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <div className="staging-alert" data-testid="staging-payment-alert">
          <strong>{modeLabel.toUpperCase()}</strong>
          <span>
            {config?.midtrans_is_production
              ? "This charges a real credit card. Cards, VA, GoPay and QRIS accepted via Midtrans."
              : "Test cards only. Try 4811 1111 1111 1114 / CVV 123 / OTP 112233."}
          </span>
        </div>
        {error && <p data-testid="checkout-error" className="error-message">{error}</p>}
        <button
          data-testid="checkout-pay-button"
          className="button primary full"
          onClick={pay}
          disabled={!email || busy}
        >
          {busy ? "Opening secure payment…" : "Pay securely with Midtrans"} <span>→</span>
        </button>
      </div>
      <aside className="receipt">
        <p className="eyebrow">YOUR RECEIPT</p>
        <h3>{cart.name}</h3>
        <p>Digital gift card</p>
        <div className="receipt-line"><span>Total</span><strong>{money(cart.amount)}</strong></div>
        <small>Codes are issued after verified payment and emailed to the recipient.</small>
      </aside>
    </main>
  );
}

function Success() {
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const orderId = sessionStorage.getItem("kado-order-id");

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await request(`/orders/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        if (data.status !== "paid" && tries < 20) {
          setTimeout(() => setTries((t) => t + 1), 2500);
        }
      } catch (err) {
        if (!cancelled) setOrder({ error: err.message });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, tries]);

  if (!orderId) return <main className="shell page"><h1>No order found.</h1></main>;
  if (!order) return <main className="shell page"><p data-testid="order-loading">Confirming your payment…</p></main>;
  if (order.error) return <main className="shell page"><p data-testid="order-error" className="error-message">{order.error}</p></main>;

  const paid = order.status === "paid";
  const copy = () => order.gift_code && navigator.clipboard?.writeText(order.gift_code);

  return (
    <main className="shell page success-page">
      <p className="eyebrow">{paid ? "IT'S ON ITS WAY" : "STILL WORKING"}</p>
      <h1>{paid ? <>A gift worth<br /><em>opening.</em></> : <>Hold tight,<br /><em>almost there.</em></>}</h1>
      <p data-testid="order-success-message">
        {paid
          ? `Your gift card has been emailed to ${order.recipient_email}. You can also share the code below.`
          : "Your payment is being confirmed by Midtrans. This page will update automatically."}
      </p>
      {paid && order.gift_code && (
        <div className="code-block">
          <span>GIFT CODE</span>
          <strong data-testid="gift-code-value">{order.gift_code}</strong>
          <button data-testid="copy-gift-code-button" onClick={copy}>Copy code</button>
        </div>
      )}
      <div className="order-meta">
        <span data-testid="order-number">Order {order.order_id}</span>
        <span data-testid="order-status">Status: {order.status}</span>
      </div>
      <Link data-testid="success-shop-button" className="button secondary" to="/gift-cards">Choose another gift ↗</Link>
    </main>
  );
}

function About() {
  return (
    <main className="shell page info-page">
      <p className="eyebrow" data-testid="about-eyebrow">OUR LITTLE CORNER OF THE INTERNET</p>
      <h1 data-testid="about-heading">Gifts should feel<br /><em>like a gesture.</em></h1>
      <div className="prose">
        <p data-testid="about-intro">
          KadoKita is a small Indonesian gift-card shop for the moments that deserve a little more than a message.
          We help you choose something useful and personal — a card that arrives with your words, ready to be spent
          on the things they love.
        </p>
        <h2>Why we started</h2>
        <p>
          Digital gifting had become cold. Generic PDFs, boring emails, and the same tired designs. We wanted to
          bring back the feeling of handing someone a beautifully wrapped gift — the kind that makes them pause
          before opening. So we built KadoKita: paper-inspired cards, warm typography, and codes that feel earned,
          not automated.
        </p>
        <h2>How we think about trust</h2>
        <p>
          Every payment is processed by <strong>Midtrans</strong>, Indonesia's largest payment gateway. Your card
          details never touch our servers — Midtrans handles the entire secure flow with 3D Secure and bank-grade
          encryption. Gift codes are only released once a payment is verified through Midtrans's signed webhook.
        </p>
        <h2>Who we are</h2>
        <p>
          We're a small team based in Indonesia, obsessed with making digital things feel a little more human.
          If you have thoughts, complaints, or ideas — we want to hear them. Say hello on our <Link to="/contact">contact page</Link>.
        </p>
        <ul className="fact-list">
          <li><b>Founded</b><span>2026, Indonesia</span></li>
          <li><b>Payments</b><span>Midtrans (Cards, VA, GoPay, QRIS)</span></li>
          <li><b>Delivery</b><span>Instant email to recipient</span></li>
          <li><b>Support</b><span>Reply within 24 hours</span></li>
        </ul>
      </div>
    </main>
  );
}

function Terms() {
  return (
    <main className="shell page info-page">
      <p className="eyebrow" data-testid="terms-eyebrow">TERMS OF SERVICE</p>
      <h1 data-testid="terms-heading">A few things<br /><em>worth knowing.</em></h1>
      <div className="prose">
        <p data-testid="terms-intro">
          These terms govern your use of KadoKita and any purchase made through this website. By creating an
          account or placing an order, you agree to the following.
        </p>

        <h2>1. About KadoKita</h2>
        <p>
          KadoKita is a digital gift-card marketplace operated in Indonesia. Product availability, prices, and
          card validity are shown on the checkout page and confirmed by email once payment is complete.
        </p>

        <h2>2. Accounts</h2>
        <p>
          You are responsible for keeping your account credentials safe. Passwords are stored using bcrypt and are
          never visible to us. Notify us immediately if you suspect unauthorized access.
        </p>

        <h2>3. Payments and refunds</h2>
        <p>
          All payments are processed by <strong>PT Midtrans</strong>, an OJK-licensed payment gateway. We accept
          credit and debit cards (Visa, Mastercard, JCB, American Express), Virtual Account, GoPay, and QRIS.
          KadoKita never sees or stores your card details. Because gift codes are digital and delivered instantly,
          orders are non-refundable once the code has been issued, except where required by Indonesian consumer law.
          If a code is issued but the payment is later reversed by the card issuer, we reserve the right to
          invalidate the code.
        </p>

        <h2>4. Gift codes</h2>
        <p>
          Gift codes are unique, single-use identifiers. Keep them private — we cannot replace codes shared publicly,
          screenshotted on social media, or lost due to email forwarding. Codes do not expire, but merchant terms
          for the redemption partner may apply.
        </p>

        <h2>5. Delivery</h2>
        <p>
          Gift cards are delivered by email to the recipient address provided at checkout. Delivery normally happens
          within seconds of payment confirmation. If the recipient does not receive the email, check the spam folder
          or contact us and we will re-send from a different mail relay.
        </p>

        <h2>6. Acceptable use</h2>
        <p>
          You may not use KadoKita for fraud, money laundering, purchasing on behalf of sanctioned parties, or any
          activity that violates Indonesian law. Suspicious orders may be held for manual review.
        </p>

        <h2>7. Privacy</h2>
        <p>
          We collect only the information needed to fulfil your order: your email, the recipient's email, and the
          minimum transaction data required by Midtrans. We do not sell your data. Emails from KadoKita are sent
          from <code>noreply@lang-ber.com</code>.
        </p>

        <h2>8. Liability</h2>
        <p>
          KadoKita's total liability for any claim relating to a purchase is limited to the amount you paid for that
          gift card. We are not liable for third-party merchant behaviour, extended email deliverability delays, or
          losses arising from codes you have shared publicly.
        </p>

        <h2>9. Changes to these terms</h2>
        <p>
          We may update these terms occasionally. Material changes will be announced on this page with an updated
          effective date. Continued use of KadoKita after a change means you accept the new terms.
        </p>

        <h2>10. Contact</h2>
        <p>
          Questions or concerns? Reach us on our <Link to="/contact">contact page</Link>. We reply within one
          business day.
        </p>

        <p className="fine-print" data-testid="terms-effective-date">Effective date: February 2026</p>
      </div>
    </main>
  );
}

function ContactPage() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await request("/contact", { method: "POST", body: JSON.stringify(form) });
      setSent(true);
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <main className="shell page info-page">
      <p className="eyebrow" data-testid="contact-eyebrow">WE'RE HERE TO HELP</p>
      <h1 data-testid="contact-heading">Say <em>hello.</em></h1>
      <div className="prose">
        <p data-testid="contact-intro">
          Questions about a gift, an order, or a thoughtful idea? Send us a note and our small team will get back
          to you within one business day.
        </p>
        {sent ? (
          <p data-testid="contact-success" className="success-message">Thanks — your note is safely with us.</p>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="contact-name">Name</label>
            <input
              data-testid="contact-name-input"
              id="contact-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <label htmlFor="contact-email">Email</label>
            <input
              data-testid="contact-email-input"
              id="contact-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <label htmlFor="contact-message">Message</label>
            <textarea
              data-testid="contact-message-input"
              id="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
            />
            {error && <p data-testid="contact-error" className="error-message">{error}</p>}
            <button data-testid="contact-submit-button" className="button primary">Send note ↗</button>
          </form>
        )}
      </div>
    </main>
  );
}

function Account({ user }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => { if (user) request("/orders").then(setOrders).catch(() => {}); }, [user]);
  return (
    <main className="shell page account-page">
      <p className="eyebrow">YOUR KADOKITA</p>
      <h1>Good things,<br /><em>kept close.</em></h1>
      <p data-testid="account-email">Signed in as {user?.email}</p>
      <div className="order-list">
        <h2>Order history</h2>
        {orders.length ? orders.map((order) => (
          <div data-testid={`account-order-${order.order_id}`} className="order-row" key={order.order_id}>
            <span>{order.product_name || order.product}<small>{order.order_id} · {order.status}</small></span>
            <strong>{order.gift_code || (order.status === "paid" ? "—" : "pending")}</strong>
          </div>
        )) : <p data-testid="empty-order-history">Your first thoughtful order will appear here.</p>}
      </div>
    </main>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("kado-token");
    if (token) request("/auth/me").then(setUser).catch(() => sessionStorage.removeItem("kado-token"));
    request("/config").then(setConfig).catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <Layout user={user} setUser={setUser}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/gift-cards" element={<Shop />} />
          <Route path="/gift-cards/:slug" element={<Product />} />
          <Route path="/checkout" element={<Checkout user={user} config={config} />} />
          <Route path="/order-success" element={<Success />} />
          <Route path="/login" element={<Auth mode="login" setUser={setUser} />} />
          <Route path="/register" element={<Auth mode="register" setUser={setUser} />} />
          <Route path="/account" element={<Account user={user} />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<Terms />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
